import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { getDisplayName, getPrincipalInitials } from '../../../../../../models/NameUtils';
import { getMakeContactPrivateError } from '../../../../../../models/utils/contactUtils';
import {
	invalidateAllContactFilterQueries,
	useContact,
	useContactSetKeepInTouchMutation,
} from '../../../../../../queries';
import { useContactDeleteKeepInTouchMutation } from '../../../../../../queries/Contact/useContactDeleteKeepInTouchMutation';
import { useContactDeleteMutation } from '../../../../../../queries/Contact/useContactDeleteMutation';
import { useContactKeepInTouchQuery } from '../../../../../../queries/Contact/useContactKeepInTouchQuery';
import { Button } from '../../../../../components-v2/Button';
import { Avatar } from '@HoudiniUI/Avatar';
import { DeleteConfirmation } from '../../../../../components/DeleteConfirmation';
import { LoadingSpinner } from '../../../../../components/LoadingSpinner';
import { ChevronLeft } from '../../../../../components/svgs/icons/ChevronLeft';
import { ExpandedViewIcon } from '../../../../../components/svgs/icons/ExpandedViewIcon';
import { ResourceVisibility, VisibilityDropdown } from '../../../../../components/VisibilityDropdown';
import { ROUTES } from '../../../../../constants/routes';
import { bs } from '../../../../../styles/styles';
import { useUpdateContactHelper } from '../../hooks/useUpdateContactHelper';
import { ContactDetails } from './components/ContactDetails';
import { CustomFields } from './components/CustomFields';
import { EditableContactForm } from './components/EditableContactForm';
import { JobTitleInfo } from './components/JobTitleInfo';
import { KeepInTouch } from './components/KeepInTouch';
import { KeepInTouchAgentScore } from './components/KeepInTouchAgentScore';
import { KeyFacts } from './components/KeyFacts/KeyFacts';
import { QuickActions } from './components/QuickActions';
import { Tags } from './components/Tags';
import { styleSheet } from './styles';
import { Tooltip, TooltipContent, TooltipTrigger } from '@WebComponents/Tooltip';
import { QuestionMarkIcon } from '@WebComponents/svgs/icons/QuestionMarkIcon';
import { EngagementStats } from '@WebComponents/contacts/EngagementStats';
import { LastInteractionInfo } from '@WebComponents/contacts/LastInteractionInfo';

type Props = {
	contactId: string;
	hideFullScreenButton?: boolean;
	onDeleted?: () => void;
};

type CustomFieldWithValue = Api.IFormField<string> & {
	value: string;
};

type ContactWithKitFrequency = Partial<Api.IContact> & {
	kitFrequency?: number;
	customFields?: CustomFieldWithValue[];
};

export const ContactInfo = ({ contactId, hideFullScreenButton = false, onDeleted }: Props) => {
	const [isEditing, setIsEditing] = useState(false);
	const [currentKitFrequency, setCurrentKitFrequency] = useState<number>(-1);
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
	const [requestedVisibility, setRequestedVisibility] = useState<ResourceVisibility | null>(null);
	const pendingCustomFieldsRef = useRef<CustomFieldWithValue[]>([]);
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();
	const history = useHistory();
	const isOnContactsPage = useRouteMatch(ROUTES.contacts.people)?.isExact;
	const isOnContactPage = useRouteMatch(`${ROUTES.contacts.people}/${contactId}`)?.isExact;
	const showFullScreenButton = !isOnContactPage && !hideFullScreenButton;
	const [origin] = useQueryState('origin', parseAsString.withDefault(''));
	const showBackToContactsButton = isOnContactPage && origin === 'contacts';

	const {
		data: contactQuery,
		isPending,
		refetch: refetchContact,
	} = useContact({
		contactId,
		enabled: Boolean(contactId),
		expandHouseholdMembers: true,
		allowArchived: true,
	});

	const { data: keepInTouchQuery, refetch: refetchKeepInTouch } = useContactKeepInTouchQuery({
		contactId: contactId || '',
		enabled: Boolean(contactId),
	});

	const updateContactMutation = useUpdateContactHelper({
		onSuccess: async () => {
			// Custom fields are now saved as part of the main contact update
			// So we don't need to save them separately anymore
			toast.success('Contact updated successfully');
			setIsEditing(false);
			await refetchContact();
			setRequestedVisibility(null);
		},
		onError: error => {
			// Clear pending custom fields on error
			pendingCustomFieldsRef.current = [];
			setRequestedVisibility(null);

			errorMessages.pushApiError(error);
		},
	});

	const setKeepInTouchMutation = useContactSetKeepInTouchMutation({
		onSuccess: () => {
			refetchKeepInTouch();
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const deleteKeepInTouchMutation = useContactDeleteKeepInTouchMutation({
		onSuccess: () => {
			refetchKeepInTouch();
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});

	const contactDeleteMutation = useContactDeleteMutation({
		onSuccess: (result: Api.IBulkOperationResult<string>) => {
			if (result.success) {
				toast.success('Contact deleted successfully');
				if (onDeleted) {
					onDeleted();
				} else {
					history.push(ROUTES.contacts.people);
				}
			} else {
				// Handle partial failures
				const failedCount = result.failed?.length || 0;
				if (failedCount > 0) {
					toast.error(`Failed to delete contact`);
				}
			}
			setShowDeleteConfirmation(false);
			invalidateAllContactFilterQueries();
		},
		onError: (_error: Api.IBulkOperationResult<string>) => {
			toast.error(`Failed to delete contact`);
			setShowDeleteConfirmation(false);
		},
	});

	// Load current keep in touch frequency when component mounts or when editing starts
	useEffect(() => {
		if (keepInTouchQuery) {
			setCurrentKitFrequency(keepInTouchQuery.frequency || -1);
		} else {
			setCurrentKitFrequency(-1);
		}
	}, [keepInTouchQuery]);

	const contact = contactQuery as Api.IContact;

	const handleCustomFieldSaved = () => {
		// Refetch contact data to ensure custom fields are up to date
		refetchContact();
	};

	const handleSave = async (contactData: ContactWithKitFrequency) => {
		// Extract kitFrequency and customFields from contactData
		const { kitFrequency, customFields, ...actualContactData } = contactData;

		// Convert customFields to customProperties format
		const customProperties: Record<string, string> = {};
		if (customFields && customFields.length > 0) {
			customFields.forEach(field => {
				if (field.value && field.value.trim() !== '') {
					customProperties[field.name] = field.value;
				}
			});
		} else if (actualContactData.customProperties) {
			Object.assign(customProperties, actualContactData.customProperties);
		}

		// Include custom properties in the main contact update
		const contactDataWithCustomProperties = {
			...actualContactData,
			customProperties,
		};

		// Clear pending custom fields since we're saving them with the main contact
		pendingCustomFieldsRef.current = [];

		// Save the contact with custom properties
		updateContactMutation.updateContactHelper({
			contactId: contact.id,
			contactData: contactDataWithCustomProperties,
		});

		// Handle keep in touch changes if frequency changed
		if (typeof kitFrequency === 'number' && kitFrequency !== currentKitFrequency) {
			if (kitFrequency > 0) {
				// Create or update keep in touch
				const keepInTouchData: Api.IKeepInTouch = {
					contactId: contact.id,
					frequency: kitFrequency,
				};
				setKeepInTouchMutation.mutate([keepInTouchData]);
			} else if (currentKitFrequency > 0 && keepInTouchQuery?.id) {
				// Delete existing keep in touch
				deleteKeepInTouchMutation.mutate(keepInTouchQuery.id);
			}
		}
	};

	const handleViewFullScreen = (event: React.MouseEvent<HTMLButtonElement>) => {
		let path = `${ROUTES.contacts.people}/${contactId}`;
		if (isOnContactsPage) {
			path += `?origin=contacts`;
		}

		if (event.ctrlKey || event.metaKey) {
			window.open(window.location.origin + `/#${path}`);
		} else {
			history.push(path);
		}
	};
	const handleEmailChanged = (emails: Api.EmailAddress[]) => {
		handleSave({
			...contact,
			emailAddresses: emails,
			customProperties: contact.customProperties || {},
		});
	};

	const handleCancel = () => {
		setIsEditing(false);
	};

	const handleEditClick = () => {
		setIsEditing(true);
	};

	const handlePhoneNumberChanged = (phoneNumbers: Api.IPhoneNumber[]) => {
		handleSave({
			...contact,
			phoneNumbers,
			customProperties: contact.customProperties || {},
		});
	};

	const handleAddressChanged = (address: Api.IAddress) => {
		handleSave({
			...contact,
			address,
			customProperties: contact.customProperties || {},
		});
	};

	const handleWebsiteChanged = (website: string) => {
		handleSave({
			...contact,
			webSite: website,
			customProperties: contact.customProperties || {},
		});
	};

	const currentVisibility = contact?.visibility || Api.VmUtils.getDefaultVisibility(userSession.user);

	const handleVisibilityChanged = (visibility: ResourceVisibility) => {
		if (visibility === currentVisibility) {
			return;
		}

		const makePrivateError = visibility === 'admin' ? getMakeContactPrivateError(contact, userSession.user) : null;
		if (makePrivateError) {
			errorMessages.push({ messages: [makePrivateError] });
			return;
		}

		setRequestedVisibility(visibility);
		handleSave({
			...contact,
			visibility,
			customProperties: contact.customProperties || {},
		});
	};

	const handleDeleteContact = () => {
		setShowDeleteConfirmation(true);
	};

	const handleDeleteConfirmation = (shouldDelete: boolean) => {
		if (shouldDelete && contact?.id) {
			contactDeleteMutation.mutate({ ids: [contact.id] });
		} else {
			setShowDeleteConfirmation(false);
		}
	};

	if (!contactId) {
		return null;
	}

	if (isPending) {
		return <LoadingSpinner />;
	}

	if (!contactQuery) {
		return <div>Contact not found</div>;
	}

	if (isEditing) {
		return (
			<div className={css(styleSheet.editContainer)}>
				<EditableContactForm
					contact={contact}
					onSave={handleSave}
					onCancel={handleCancel}
					isLoading={
						updateContactMutation.isLoading || setKeepInTouchMutation.isPending || deleteKeepInTouchMutation.isPending
					}
					currentKitFrequency={currentKitFrequency}
				/>
			</div>
		);
	}

	return (
		<div className={css(styleSheet.container)}>
			{contact?.isArchived ? (
				<div className={css(styleSheet.archivedBanner)}>
					<span className={css(styleSheet.archivedBannerText)}>CONTACT ARCHIVED</span>
					<Tooltip>
						<TooltipTrigger>
							<QuestionMarkIcon fillColor='var(--color-danger-main)' />
						</TooltipTrigger>
						<TooltipContent>Deleted or archived contacts will not be included in any sends.</TooltipContent>
					</Tooltip>
				</div>
			) : null}

			{showBackToContactsButton || showFullScreenButton ? (
				<div className={css(styleSheet.profileSection)}>
					{showBackToContactsButton ? (
						<Button
							variant='secondary'
							size='sm'
							onClick={() => history.push(ROUTES.contacts.people)}
							styles={[bs.wFull]}
						>
							<ChevronLeft />
							Back to Contacts
						</Button>
					) : null}
					{showFullScreenButton ? (
						<Button
							variant='secondary'
							size='sm'
							styles={[styleSheet.viewFullScreenButton]}
							onClick={handleViewFullScreen}
						>
							<ExpandedViewIcon />
							View Full Screen
						</Button>
					) : null}
				</div>
			) : null}

			<div className={css(styleSheet.profileInfoSection)}>
				<div className={css(styleSheet.profileInfoAvatarSection)}>
					<Avatar
						imgSrc={contact.profilePic}
						fallbackText={getPrincipalInitials(contact)}
						styles={[styleSheet.profileInfoAvatar]}
						fallbackStyles={[styleSheet.profileInfoAvatar]}
					/>

					<Button variant='link' onClick={handleEditClick} type='button' styles={[styleSheet.editButton]}>
						Edit Profile
					</Button>
				</div>

				<div className={css(styleSheet.nameSection)}>
					<div className={css(styleSheet.nameSectionContent)}>
						<h2 className={css(styleSheet.contactName)}>{getDisplayName(contact)}</h2>
						<JobTitleInfo contact={contact} />
					</div>
				</div>
			</div>

			<div className={css(styleSheet.interactionSection)}>
				<LastInteractionInfo relationshipHealth={contact.relationshipHealth} />
				<EngagementStats emailStats={contact.emailStats} />
				<KeepInTouchAgentScore contactId={contactId} />
				<VisibilityDropdown
					className={css(styleSheet.visibilitySection)}
					disabled={updateContactMutation.isLoading}
					onVisibilityChanged={handleVisibilityChanged}
					visibility={requestedVisibility ?? currentVisibility}
				/>
			</div>

			<div className={css(styleSheet.actionsSection)}>
				<QuickActions contact={contact} />
			</div>

			<div className={css(styleSheet.keepInTouchSection)}>
				<KeepInTouch contact={contact} />
			</div>

			<div className={css(styleSheet.keyFactsSection)}>
				<KeyFacts contact={contact} refetchContact={refetchContact} />
			</div>

			<div className={css(styleSheet.customFieldsSection)}>
				<CustomFields contact={contact} onCustomFieldSaved={handleCustomFieldSaved} />
			</div>

			<div className={css(styleSheet.tagsSection)}>
				<Tags contact={contact} refetchContact={refetchContact} />
			</div>

			<div className={css(styleSheet.contactDetailsInfoSection)}>
				<ContactDetails
					contact={contact}
					compactLayout={false}
					showSendMessage={true}
					onEmailChanged={handleEmailChanged}
					onPhoneNumberChanged={handlePhoneNumberChanged}
					onAddressChanged={handleAddressChanged}
					onWebsiteChanged={handleWebsiteChanged}
					refetchContact={refetchContact}
				/>
			</div>

			<div className={css(styleSheet.deleteContactSection)}>
				<Button variant='destructive-secondary' size='sm' onClick={handleDeleteContact}>
					Delete Contact
				</Button>

				<DeleteConfirmation
					isOpen={showDeleteConfirmation}
					onFinish={handleDeleteConfirmation}
					bodyText={`Are you sure you want to delete ${getDisplayName(contact)}? This action cannot be undone.`}
					showSpinner={contactDeleteMutation.isPending}
				/>
			</div>
		</div>
	);
};
